import { DrawerComponent } from "../../../components/UI/Drawer/DrawerComponent";
import addOrganizationStyles from "./AddOrganization.module.scss";
import { Organization } from "../../../models/Organization";
import { EMAIL_REGEX } from "../../../helpers/constants";
import { US_STATE_OPTIONS } from "../../../helpers/usStates";
import { useDispatch, useSelector } from "react-redux";
import {
  addOrganizationThunk,
  organizationSelector,
  updateOrganizationThunk,
} from "../../../slices/SuperAdmin/OrganisationList.slice";
import { useEffect, useState } from "react";
import { showSuccess } from "../../../helpers/Notifications/Notification";
import ErrorAlert from "../../../components/UI/ErrorAlert/ErrorAlert";
import { Button, Checkbox, Input, Select } from "../../../components/ds";

class AddOrganizationProps {
  /**
   * @type {boolean}
   */
  open;

  /**
   * @type {?Organization}
   */
  organization;

  /**
   * @type {boolean}
   */
  isEdit;

  /**
   * @callback onCloseCallback
   */
  /**
   * @type {onCloseCallback}
   */
  onClose;

  /**
   * @callback onRefreshCallback
   */
  /**
   * @type {onRefreshCallback}
   */
  onRefresh;
  /**
   * @type {number}
   */
  stateAdminId;
}

/**
 *
 * @param {AddOrganizationProps} props
 * @returns {JSX.Element}
 */
export default function AddOrganization(props) {
  (() => {
    if (props.isEdit && !props.organization) {
      throw new Error("organization prop can't be null when isEdit is true");
    }
  })();

  return (
    <DrawerComponent
      title={
        <div className={addOrganizationStyles.titleContainer}>
          <div className={addOrganizationStyles.title}>
            {props.isEdit ? "Edit Organization" : "Add Organization"}
          </div>
          <div className={addOrganizationStyles.subtitle}>
            Fill the following fields to add an organization.
          </div>
          <span className={addOrganizationStyles.divider}></span>
        </div>
      }
      content={
        <AddOrganizationContent
          organization={props.organization}
          onCancelClick={props.onClose}
          onRefresh={props.onRefresh}
          isEdit={props.isEdit}
          organizationTypes={props.organizationTypes}
          stateAdminId={props.stateAdminId}
        />
      }
      drawerVisible={props.open}
      onClose={props.onClose}
    />
  );
}

function buildInitialValues(organization) {
  return {
    name: organization ? organization.organizationName : "",
    facilitator_name: organization ? organization.facilitatorName : "",
    email: organization ? organization.facilitatorEmail : "",
    organization_type: organization ? organization.organization_type : "",
    state: organization ? organization.state ?? "" : "",
    sendEmail: true,
  };
}

function validateOrgFields(values) {
  const errors = {};
  if (!values.name?.trim()) {
    errors.name = 'Please fill "Name of Organization" to proceed!';
  }
  if (!values.facilitator_name?.trim()) {
    errors.facilitator_name = 'Please fill "Facilitator’s Name" to proceed!';
  }
  if (
    values.organization_type === "" ||
    values.organization_type == null
  ) {
    errors.organization_type = "Please fill Organization type to proceed!";
  }
  if (!values.email?.trim()) {
    errors.email = "Please fill email-id to proceed!";
  } else if (!EMAIL_REGEX.test(values.email)) {
    errors.email = "Invalid Email ID";
  }
  return errors;
}

/**
 *
 * @param {Object} param0
 * @param {Function} param0.onCancelClick
 * @param {Organization} param0.organization
 * @param {boolean} param0.isEdit
 * @param {Function} param0.onRefresh
 * @returns {JSX.Element}
 */
function AddOrganizationContent({
  onCancelClick,
  organization,
  isEdit,
  onRefresh,
  organizationTypes,
  stateAdminId,
}) {
  const dispatch = useDispatch();
  const state = useSelector(organizationSelector);

  const [currentEmail, setCurrentEmail] = useState(
    organization?.facilitatorEmail ?? ""
  );
  const [orgError, setOrgError] = useState();
  const [values, setValues] = useState(() => buildInitialValues(organization));
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setValues(buildInitialValues(organization));
    setCurrentEmail(organization?.facilitatorEmail ?? "");
    setErrors({});
    setOrgError(undefined);
  }, [organization, isEdit]);

  const fieldError = (name) =>
    errors[name] ||
    (orgError && typeof orgError === "object" ? orgError[name] : null);

  const handleChange = (name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleFormSubmit = (event) => {
    event.preventDefault();
    const nextErrors = validateOrgFields(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const resolvedTypeId = (() => {
      const raw = values.organization_type;
      if (raw === "" || raw == null) return raw;
      const byId = (organizationTypes || []).find(
        (t) => String(t.id) === String(raw)
      );
      if (byId) return byId.id;
      const byLabel = (organizationTypes || []).find((t) => t.type === raw);
      return byLabel ? byLabel.id : raw;
    })();

    const org = Object.assign(new Organization(), organization);
    org.organizationName = values.name;
    org.facilitatorName = values.facilitator_name;
    org.facilitatorEmail = values.email;
    org.sendEmail = values.sendEmail;
    org.organization_type_id = Number(resolvedTypeId);
    org.state = values.state === "" ? null : values.state ?? null;

    if (org.id !== 0) {
      handlePut(org);
    } else {
      handlePost(org);
    }
  };

  /**
   *
   * @param {Organization} org
   */
  const handlePut = (org) => {
    org.currentEmail = currentEmail;
    dispatch(updateOrganizationThunk({ ...org, stateAdminId })).then((data) => {
      if (
        data?.payload.isAxiosError ||
        data?.payload?.error ||
        data?.payload?.detail
      ) {
        setOrgError(
          data?.payload?.error ||
            data?.payload ||
            data?.payload?.response?.data?.message ||
            data?.payload?.message ||
            "Something went wrong"
        );
      } else {
        onRefresh("edit");
        showSuccess(
          <div>
            Organization updated successfully! <br />
            {currentEmail !== org.facilitatorEmail && (
              <span>An email has been sent with the credentials.</span>
            )}
          </div>
        );
        onCancelClick();
      }
    });
  };

  /**
   *
   * @param {Organization} org
   */
  const handlePost = (org) => {
    dispatch(addOrganizationThunk({ ...org, stateAdminId })).then((data) => {
      if (data?.payload.isAxiosError || data?.payload?.error) {
        setOrgError(
          data?.payload?.error ||
            data?.payload?.response?.data?.message ||
            data?.payload?.message ||
            "Something went wrong"
        );
      } else {
        onRefresh("add");
        showSuccess(
          <div>
            Organization added successfully! <br />
            {values.sendEmail && (
              <span>An email has been sent with the credentials.</span>
            )}
          </div>
        );
        onCancelClick();
      }
    });
  };

  const orgTypeOptions = (organizationTypes || []).map((t) => ({
    value: t.id,
    label: t.type,
  }));

  const stateOptions = US_STATE_OPTIONS.map((s) => ({
    value: s.value,
    label: s.name,
  }));

  // organization_type from edit may be a display string; prefer matching id when possible
  const orgTypeValue = (() => {
    const raw = values.organization_type;
    if (raw === "" || raw == null) return "";
    const byId = orgTypeOptions.find((o) => String(o.value) === String(raw));
    if (byId) return byId.value;
    const byLabel = orgTypeOptions.find((o) => o.label === raw);
    return byLabel ? byLabel.value : raw;
  })();

  return (
    <div className="platformAdminAddAndEditForm">
      <ErrorAlert errorData={orgError} />
      <form
        className="ops-form-stack"
        autoComplete="off"
        noValidate
        onSubmit={handleFormSubmit}
      >
        <div className="ops-field">
          <label className="ops-field-label" htmlFor="org-name">
            Name of Organization
          </label>
          <Input
            id="org-name"
            name="name"
            value={values.name}
            placeholder="Name of Organization"
            invalid={Boolean(fieldError("name"))}
            onChange={(e) => handleChange("name", e.target.value)}
          />
          {fieldError("name") ? (
            <p className="ops-field-error">{fieldError("name")}</p>
          ) : null}
        </div>

        <div className="ops-field">
          <label className="ops-field-label" htmlFor="org-facilitator">
            Facilitator’s Name
          </label>
          <Input
            id="org-facilitator"
            name="facilitator_name"
            value={values.facilitator_name}
            placeholder="Facilitator’s Name"
            invalid={Boolean(fieldError("facilitator_name"))}
            onChange={(e) => handleChange("facilitator_name", e.target.value)}
          />
          {fieldError("facilitator_name") ? (
            <p className="ops-field-error">{fieldError("facilitator_name")}</p>
          ) : null}
        </div>

        <div className="ops-field">
          <label className="ops-field-label" htmlFor="org-type">
            Organization Type
          </label>
          <Select
            id="org-type"
            value={orgTypeValue}
            placeholder="Organization Type"
            options={orgTypeOptions}
            onChange={(value) => handleChange("organization_type", value)}
          />
          {fieldError("organization_type") ? (
            <p className="ops-field-error">{fieldError("organization_type")}</p>
          ) : null}
        </div>

        <div className="ops-field">
          <label className="ops-field-label" htmlFor="org-state">
            Jurisdiction (state for incident reporting)
          </label>
          <Select
            id="org-state"
            value={values.state ?? ""}
            options={stateOptions}
            onChange={(value) => handleChange("state", value)}
          />
        </div>

        <div className="ops-field">
          <label className="ops-field-label" htmlFor="org-email">
            Email ID
          </label>
          <Input
            id="org-email"
            name="email"
            type="email"
            value={values.email}
            placeholder="Email ID"
            invalid={Boolean(fieldError("email"))}
            onChange={(e) => handleChange("email", e.target.value)}
          />
          {fieldError("email") ? (
            <p className="ops-field-error">{fieldError("email")}</p>
          ) : null}
        </div>

        {!isEdit && (
          <div className={addOrganizationStyles.confirmation}>
            <Checkbox
              className={addOrganizationStyles.checkbox}
              checked={!!values.sendEmail}
              onChange={(e) => handleChange("sendEmail", e.target.checked)}
            >
              An email with login credentials will be sent to organization Email
              ID
            </Checkbox>
          </div>
        )}

        <div className={addOrganizationStyles.actionContainer}>
          <Button type="button" variant="secondary" onClick={onCancelClick}>
            CANCEL
          </Button>
          <Button type="submit" disabled={state.isAddOrEditLoading}>
            {state.isAddOrEditLoading
              ? isEdit
                ? "UPDATING…"
                : "ADDING…"
              : isEdit
                ? "UPDATE"
                : "ADD"}
          </Button>
        </div>
      </form>
    </div>
  );
}
